// Read Matrix table pricing without assuming every first-column label is numeric.
// Some embroidery tables have an explicit "Each Additional 1000 over 10000"
// surcharge row after the last stitch-count tier.
// Production inputs (stitches, colors, etc.) must be non-negative whole
// numbers. Validate separately from NET price values, which can have decimals.
// Keep values within JS safe-integer range to prevent inaccurate tier math.
export const isValidDynamicWholeNumber = (value) => {
  if (typeof value === "number") return Number.isSafeInteger(value) && value >= 0;
  if (typeof value !== "string" || !/^\d+$/.test(value)) return false;
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric >= 0;
};

const numericValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const text = value.trim().replace(/,/g, "");
  if (!/^-?\$?\d+(?:\.\d+)?$/.test(text)) return null;
  const amount = Number(text.replace("$", ""));
  return Number.isFinite(amount) ? amount : null;
};

const parseRange = (key) => {
  const match = String(key).match(/^\s*(\d[\d,]*)\s*-\s*(\d[\d,]*)\s*$/);
  if (!match) return null;
  const min = numericValue(match[1]);
  const max = numericValue(match[2]);
  return min > 0 && max >= min ? { min, max } : null;
};

const parseOverageRule = (label) => {
  const match = String(label || "").match(
    /\beach\s+additional\s+(\d[\d,]*)\s+(?:stitches?\s+)?(?:over|above)\s+(\d[\d,]*)\b/i
  );
  if (!match) return null;
  const increment = numericValue(match[1]);
  const threshold = numericValue(match[2]);
  return increment > 0 && threshold >= 0 ? { increment, threshold } : null;
};

const priceForQuantity = (row, quantity) => {
  if (!row || typeof row !== "object" || !Number.isFinite(quantity) || quantity < 1) return null;
  for (const [key, value] of Object.entries(row)) {
    const range = parseRange(key);
    if (!range || quantity < range.min || quantity > range.max) continue;
    return numericValue(value);
  }
  // Historical single-price run tables have no quantity ranges.
  return Object.prototype.hasOwnProperty.call(row, "Price")
    ? numericValue(row.Price)
    : null;
};

const errorResult = (error) => ({
  row: null,
  surchargeRow: null,
  surchargeSteps: 0,
  netCost: null,
  error,
});

/**
 * Resolve one Matrix table charge. The first column is a production input
 * (stitches, colors, etc.); other columns are piece-quantity ranges.
 * Only a deliberately labeled overage row enables extrapolation.
 */
export const resolveDecorationTablePrice = ({
  rows = [],
  inputHeader,
  inputValue,
  quantity = 1,
  chargeType = "Run",
} = {}) => {
  if (!isValidDynamicWholeNumber(inputValue)) {
    return errorResult("Enter a whole number (0 or greater).");
  }
  const value = Number(inputValue);
  const qty = numericValue(quantity);
  // Zero is valid as an input, but cannot receive a positive-tier price.
  if (value === 0) return errorResult("No pricing tier is configured for 0.");
  if (qty === null || qty <= 0) return errorResult("Enter a valid order quantity.");
  if (!inputHeader || !Array.isArray(rows)) return errorResult("This charge has no usable pricing table.");

  const tiers = rows
    .filter((row) => row && typeof row === "object")
    .map((row) => ({ row, limit: numericValue(row[inputHeader]) }))
    .filter(({ limit }) => limit !== null && limit > 0)
    .sort((a, b) => a.limit - b.limit);

  let previous = 0;
  for (const tier of tiers) {
    if (value > previous && value <= tier.limit) {
      const netCost = chargeType === "Fixed"
        ? numericValue(tier.row.Cost)
        : priceForQuantity(tier.row, qty);
      return netCost === null
        ? errorResult("No price is configured for this order quantity.")
        : { row: tier.row, surchargeRow: null, surchargeSteps: 0, netCost, error: null };
    }
    previous = tier.limit;
  }

  // Never guess an overage. Require an explicit surcharge row and the
  // matching base tier at its stated threshold.
  if (chargeType === "Run") {
    for (const row of rows) {
      if (!row || typeof row !== "object") continue;
      const rule = parseOverageRule(row[inputHeader]);
      if (!rule || value <= rule.threshold) continue;
      // Do not bypass a higher explicit tier with surcharge pricing.
      if (tiers.some((tier) => tier.limit > rule.threshold)) continue;
      const base = tiers.find((tier) => tier.limit === rule.threshold);
      if (!base) continue;
      const basePrice = priceForQuantity(base.row, qty);
      const extraPrice = priceForQuantity(row, qty);
      if (basePrice === null || extraPrice === null) {
        return errorResult("No base or additional-stitch price is configured for this order quantity.");
      }
      const surchargeSteps = Math.ceil((value - rule.threshold) / rule.increment);
      const netCost = Math.round((basePrice + surchargeSteps * extraPrice + Number.EPSILON) * 100) / 100;
      return {
        row: base.row,
        surchargeRow: row,
        surchargeSteps,
        netCost,
        increment: rule.increment,
        threshold: rule.threshold,
        error: null,
      };
    }
  }
  return errorResult("No pricing tier covers this production value. Check the Matrix before saving.");
};
